// File: js/ui/engine-boot.js
//
// THE ONLY PLACE THE MONEY ENGINE IS WIRED.
//
// Spec §13 step 4: "Rewire the cashier, receipt, and reporting modules onto the
// engine. They add no storage or sync logic of their own — that firewall is the
// whole point." This module is that firewall's single door. It constructs the
// Supabase client, builds the real transport, creates the sync engine, requests
// persistent storage, provisions the device, ensures a receipt block, and hands
// the UI modules ONE handle.
//
// Audit C5 is what happens without it: `db.version(1)` declared in four modules on
// one database name with two different primary keys, so after the first logout
// every offline save threw `DataError` until browser storage was cleared. There is
// now exactly one Dexie handle (`db/schema.js`) and exactly one sync engine (here).
//
// ── THE COLLECTION GATE ─────────────────────────────────────────────────────
// Spec §7 orders it: durability, THEN identity, THEN receipt supply.
//
//   1. storage not persistent  → the browser may evict cash we have taken
//   2. device not provisioned  → a parked event would carry a null device_id and
//                                appear in NO device's conflict queue — invariant 6
//                                violated by omission (spec §7, added 27 Jul 2026)
//   3. no receipt numbers      → nothing left in this device's block
//
// `db/ledger-write.js#collectionAllowed` implements 1 and 3. The identity check
// lives here because the device identity is a boot-time fact, and because putting
// it anywhere else would let an unprovisioned device pass a gate that says yes.

import {
    db,
    createSupabaseTransport,
    createSyncEngine,
    createUnloadGuard,
    requestPersist,
    isPersistGranted,
    collectionAllowed,
    getDeviceId,
    setDeviceId,
    readBlockState,
    maybeRequestBlock,
    pendingSummary
} from '../db/index.js';

import { getSupabaseClient, renderConfigError } from '../supabaseClient.js';

/** How a freshly registered device names itself, per role. */
const DEVICE_LABEL = Object.freeze({
    admin: 'Administration',
    cashier_a: 'Caisse A — régie',
    cashier_b: 'Caisse B — pharmacie'
});

/** @type {object|null} The one engine. */
let engine = null;
/** @type {Promise<object>|null} In-flight boot, so concurrent callers share it. */
let booting = null;

/**
 * The engine, or `null` before boot.
 *
 * Callers that can tolerate "not yet" use this; callers that cannot must await
 * `bootEngine()`.
 * @returns {object|null}
 */
export function getEngine() {
    return engine;
}

/** The shared Dexie handle. Exported so a UI module never constructs its own. */
export { db };

/**
 * Boot the money engine. Idempotent: the second call returns the first result.
 *
 * Must be called with a live session — device registration and block allocation
 * are authenticated RPCs — so `app.js` calls it once a session is known.
 *
 * @param {{
 *   role?: string|null,
 *   deviceName?: string|null,
 *   client?: object,
 *   database?: import('dexie').Dexie,
 *   navigator?: object,
 *   autoStart?: boolean
 * }} [options]
 * @returns {Promise<object>} The engine handle.
 */
export function bootEngine(options = {}) {
    if (engine) return Promise.resolve(engine);
    if (booting) return booting;
    booting = build(options).finally(() => { booting = null; });
    return booting;
}

/**
 * Tear the engine down (sign-out). The DATA is untouched — `db/logout.js` owns
 * what may and may not be cleared, and the answer is never the ledger or outbox.
 */
export function shutdownEngine() {
    if (engine && engine.sync) engine.sync.stop();
    engine = null;
}

/**
 * @param {object} options
 * @returns {Promise<object>}
 */
async function build(options) {
    const database = options.database || db;
    const listeners = new Set();

    // ---- 1. the client -----------------------------------------------------
    let client = options.client || null;
    /** @type {Error|null} */
    let configError = null;
    if (!client) {
        try {
            client = getSupabaseClient();
        } catch (error) {
            // A misconfigured build must not take the bundle down at import time;
            // it renders a banner and the desk falls back to the paper book.
            configError = /** @type {Error} */ (error);
            renderConfigError(typeof document !== 'undefined' ? document : undefined);
        }
    }

    const transport = client ? createSupabaseTransport(client) : null;

    // ---- 2. durability (gate step 1) ---------------------------------------
    // Asked FIRST and before any write can happen. A refusal is not fatal: the
    // device simply refuses to collect, which is the correct answer.
    let persistGranted = false;
    try {
        persistGranted = await requestPersist(database, { navigator: options.navigator });
    } catch {
        persistGranted = false;
    }

    // ---- 3. identity (gate step 2) -----------------------------------------
    const deviceId = await ensureDevice(database, transport, options);

    // ---- 4. receipt supply (gate step 3) -----------------------------------
    if (transport && deviceId) {
        try {
            await maybeRequestBlock(database, transport, deviceId);
        } catch {
            // Offline or refused: the device keeps saying « Plus de numéros de
            // reçu — utilisez le carnet papier. » until a block arrives.
        }
    }

    // ---- 5. the sync engine -------------------------------------------------
    const sync = transport
        ? createSyncEngine({ db: database, transport, deviceId, navigator: options.navigator })
        : null;

    engine = {
        db: database,
        client,
        transport,
        sync,
        deviceId,
        configError,
        persistGranted,

        /** Register a listener for status changes. Returns an unsubscribe fn. */
        onStatus(listener) {
            if (typeof listener !== 'function') return () => {};
            listeners.add(listener);
            return () => listeners.delete(listener);
        },

        /** Recompute the status and notify every listener. */
        async publishStatus() {
            const snapshot = await engineStatus();
            for (const listener of listeners) {
                try { listener(snapshot); } catch { /* a broken widget must not stop the rest */ }
            }
            return snapshot;
        },

        /** Push + pull now. Safe to call when the transport is missing. */
        async kick() {
            if (sync) await sync.kick();
            return engine.publishStatus();
        }
    };

    if (sync && typeof addEventListener === 'function') {
        // Warn while this device holds cash the server has never seen.
        addEventListener('beforeunload', createUnloadGuard(sync));
    }

    if (sync && options.autoStart !== false) {
        // Deliberately not awaited: the first round pulls full history and the
        // desk must not be blocked behind it.
        void sync.start().then(() => engine && engine.publishStatus());
    }

    await engine.publishStatus();
    return engine;
}

/**
 * Resolve this device's identity, registering it when we are allowed to.
 *
 * `register_device` is admin-only (migration 0005), so a cashier device is
 * provisioned by an admin — either by signing in once on the device, or with
 * `adoptDeviceId()`. Until then the gate refuses with `device-not-provisioned`
 * and the desk uses the paper book. That refusal is the point: a sale queued by an
 * unprovisioned device parks server-side under a null `device_id` and appears in
 * NO device's queue.
 *
 * @param {import('dexie').Dexie} database
 * @param {object|null} transport
 * @param {object} options
 * @returns {Promise<string|null>}
 */
async function ensureDevice(database, transport, options) {
    const existing = await getDeviceId(database);
    if (existing) return existing;
    if (!transport || options.role !== 'admin') return null;

    const name = options.deviceName || DEVICE_LABEL[options.role] || 'Appareil CMA Ombessa';
    try {
        const result = await transport.registerDevice(name);
        const id = result && (result.device_id || result.id);
        if (!id) return null;
        await setDeviceId(database, id);
        return id;
    } catch {
        // Offline, or the account is not an admin. Unprovisioned is a state we
        // report honestly, never one we fake our way past.
        return null;
    }
}

/**
 * Adopt a device identity an admin issued elsewhere.
 *
 * Refuses to overwrite an existing identity: re-pointing a device that has already
 * consumed receipt numbers at a different device would make its numbers fall
 * outside the new device's blocks (`receipt-no-outside-device-blocks`) and park
 * every subsequent sale.
 *
 * @param {string} deviceId
 * @returns {Promise<{adopted:boolean, reason?:string}>}
 */
export async function adoptDeviceId(deviceId) {
    if (typeof deviceId !== 'string' || deviceId.trim() === '') {
        return { adopted: false, reason: 'invalid-device_id' };
    }
    const database = engine ? engine.db : db;
    const existing = await getDeviceId(database);
    if (existing && existing !== deviceId) {
        return { adopted: false, reason: 'device-already-provisioned' };
    }
    await setDeviceId(database, deviceId.trim());
    if (engine) {
        engine.deviceId = deviceId.trim();
        await engine.publishStatus();
    }
    return { adopted: true };
}

/**
 * The collection gate, in spec §7 order: durability, identity, receipt supply.
 *
 * Ask this BEFORE opening a sale form, not only at submit time. A cashier who
 * fills a basket and only then learns the device cannot collect has already lost
 * the queue behind them.
 *
 * @returns {Promise<{allowed:boolean, reason?:string}>}
 */
export async function collectionGate() {
    // The ONLY rung that belongs here: `db/` cannot answer this one, because
    // there is no db handle to ask until the engine exists.
    if (!engine) return { allowed: false, reason: 'engine-not-ready' };

    // Everything else — durability, identity, receipt supply, in that order —
    // is decided in `db/ledger-write.js`. It used to be duplicated here, which
    // put a money rule in the UI layer: the anti-Ombessa firewall inverted, and
    // a silent drift hazard the day the gate's order changes in `db/`.
    // Identity is read there from `meta.device_id`, which is the right authority
    // — meta survives a reload, `engine.deviceId` does not.
    return collectionAllowed(engine.db);
}

/**
 * Everything a banner, a tile or a badge needs, in one read.
 *
 * Read from the TABLES, never from the sync engine's cached counters: those
 * refresh at the END of a round, and a write kicks a round without awaiting it, so
 * a cached read reports the previous value in exactly the seconds that matter.
 *
 * @returns {Promise<{
 *   ready:boolean, online:boolean, deviceId:string|null, persistGranted:boolean,
 *   pendingCount:number, pendingTotal:number, conflictCount:number,
 *   lastPullAt:string|null, syncState:string,
 *   block:{hasBlock:boolean, remaining:number, exhausted:boolean, low:boolean},
 *   gate:{allowed:boolean, reason?:string}
 * }>}
 */
export async function engineStatus() {
    const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;

    if (!engine) {
        return {
            ready: false,
            online,
            deviceId: null,
            persistGranted: false,
            pendingCount: 0,
            pendingTotal: 0,
            conflictCount: 0,
            lastPullAt: null,
            syncState: 'idle',
            block: { hasBlock: false, remaining: 0, exhausted: true, low: true },
            gate: { allowed: false, reason: 'engine-not-ready' }
        };
    }

    const [pending, block, persistGranted, gate, conflictCount] = await Promise.all([
        pendingSummary(engine.db),
        readBlockState(engine.db),
        isPersistGranted(engine.db),
        collectionGate(),
        // OPEN rows only: a badge that counts resolved parks never clears, and a
        // banner that never clears is a banner nobody reads.
        engine.db.table('conflicts').filter(c => c && c.status === 'open').count()
    ]);

    const syncStatus = engine.sync ? engine.sync.status() : { state: 'idle', lastPullAt: null };

    return {
        ready: true,
        online,
        deviceId: engine.deviceId,
        persistGranted,
        pendingCount: pending.count,
        pendingTotal: pending.total,
        conflictCount,
        lastPullAt: syncStatus.lastPullAt || null,
        syncState: syncStatus.state,
        block: {
            hasBlock: block.hasBlock,
            remaining: block.remaining,
            exhausted: block.exhausted,
            low: block.low
        },
        gate
    };
}
